import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getSessionId } from '../utils/session';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const UsageIndicator = ({ onUsageUpdate, onUpgradeClick }) => {
    const { token, isAuthenticated, getAuthHeaders } = useAuth();
    const [usage, setUsage] = useState({ used_today: 0, limit: 3, remaining: 3, plan: 'free', is_pro: false });
    const [loading, setLoading] = useState(true);

    const fetchUsage = useCallback(async () => {
        try {
            const sessionId = getSessionId();
            const headers = isAuthenticated ? getAuthHeaders() : {};
            
            const response = await axios.get(`${BACKEND_URL}/api/usage`, {
                params: { session_id: sessionId },
                headers
            });
            
            setUsage(response.data);
            if (onUsageUpdate) {
                onUsageUpdate(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch usage:', error);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, getAuthHeaders, onUsageUpdate]);

    useEffect(() => {
        fetchUsage();
    }, [fetchUsage, token]);

    // Expose refresh function
    useEffect(() => {
        window.refreshUsage = fetchUsage;
        return () => {
            delete window.refreshUsage;
        };
    }, [fetchUsage]);

    if (loading) {
        return null;
    }

    const percentage = (usage.used_today / usage.limit) * 100;
    const isLow = usage.remaining <= 1;
    const isExhausted = usage.remaining === 0;
    const isPro = usage.is_pro;

    return (
        <div className="flex items-center gap-2">
            <div 
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    isPro
                        ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                        : isExhausted 
                            ? 'bg-destructive/20 text-destructive border border-destructive/30'
                            : isLow 
                                ? 'bg-warning/20 text-warning border border-warning/30'
                                : 'bg-white/5 text-gray-400 border border-white/10'
                }`}
                data-testid="usage-indicator"
            >
                {isPro ? <Crown className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                <span>
                    {isPro ? 'Pro' : 'Free'} • {usage.used_today} / {usage.limit} today
                </span>
                <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div 
                        className={`h-full transition-all ${
                            isPro
                                ? 'bg-violet-500'
                                : isExhausted 
                                    ? 'bg-destructive' 
                                    : isLow 
                                        ? 'bg-warning' 
                                        : 'bg-primary'
                        }`}
                        style={{ width: `${Math.min(100, percentage)}%` }}
                    />
                </div>
            </div>
            
            {/* Upgrade button for non-pro users */}
            {!isPro && onUpgradeClick && (
                <button
                    onClick={onUpgradeClick}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white transition-all hover:scale-105"
                    data-testid="upgrade-btn-nav"
                >
                    <Crown className="w-3.5 h-3.5" />
                    <span>Unlock Pro</span>
                </button>
            )}
        </div>
    );
};
